import { css } from '@compiled/react';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { Document } from '@contentful/rich-text-types';

import { token } from '@atlaskit/tokens';

import { ContentPlatformImage } from '@/components/content-platform-image/src/content-platform-image';
import { CustomerStoryStory } from '@/content/customerStories/customerStories.types';
import { EmbeddedVideo } from '@/libraries/design/components/embedded-video/src/embedded-video';
import { PullQuote } from '@/libraries/design/components/pull-quote/src/pull-quote';
import { ColumnLayout } from '@/libraries/design/foundations';
import { RichTextComponent } from '@/libraries/design/utils';
import { DeliveryApiImage } from '@/templates/software/confluence/templates/confluence-templates-page/src/sections/template-preview-section';

type Nullable<T> = T | null;

export interface UniformBodyContentProps {
	story?: Nullable<CustomerStoryStory>;
	image?: Nullable<DeliveryApiImage>;
	imageCaption?: Nullable<string>;
	embeddedVideoLink?: Nullable<string>;
	richText?: Nullable<Document>;
}

function getRichText(richText: string | Document) {
	if (typeof richText === 'string') {
		return richText;
	}

	return documentToHtmlString(richText);
}

/**
 * @deprecated - Please use UniformBodyContent at /uniform/components/body-content instead
 */
// eslint-disable-next-line complexity
export const BodyContent = ({ story }: UniformBodyContentProps) => {
	return (
		<div
			data-testid="customer-story-article-content"
			css={css({
				display: 'flex',
				flexDirection: 'column',
				gap: token('space.300'),
			})}
		>
			{story?.storyText && (
				<div
					css={css({
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'div > :last-child': {
							marginBlockEnd: 0,
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'div > :first-child': {
							marginBlockStart: 0,
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'div > h2, h3': {
							marginBlockEnd: token('space.300'),
						},
					})}
				>
					<RichTextComponent text={getRichText(story.storyText)} />
				</div>
			)}
			{story?.quote && (
				<ColumnLayout layout="10">
					<PullQuote
						quote={story.quote.quote}
						authorName={story.quote.authorName}
						authorDetails={story.quote.authorDetails}
						alignment="start"
						variant={story.quote.heroQuote ? 'cutout' : 'basic'}
					/>
				</ColumnLayout>
			)}
			{story?.bodyAsset && story?.embeddedVideoLink && (
				<EmbeddedVideo
					embedUrl={story.embeddedVideoLink}
					thumbnail={{
						src: story.bodyAsset.url,
						alt: story.bodyAsset.description ?? '',
					}}
					title=""
					dataEventProps={{
						'data-product-key': 'uniform',
						'data-event': 'clicked',
						'data-event-component': 'embeddedVideo',
						'data-event-container': 'bodyContentInline',
					}}
				/>
			)}
			{story?.bodyAsset && !story?.embeddedVideoLink && (
				<div
					css={css({
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						gap: token('space.100'),
					})}
				>
					<ContentPlatformImage
						css={css({
							maxWidth: '100%',
							width: story.bodyAsset.width ? `${story.bodyAsset.width}px` : '100%',
							marginInline: 'auto',
						})}
						src={story.bodyAsset.url ?? ''}
						alt={story.bodyAsset.description ?? ''}
						contentType={story.bodyAsset.contentType}
						baseWidth={856}
					/>
					{story.bodyAssetCaption && (
						<p className="fine-print" css={css({ textAlign: 'center', margin: 0 })}>
							{story.bodyAssetCaption}
						</p>
					)}
				</div>
			)}
		</div>
	);
};
